<template>
  <div class="tab-bar" v-if="tabsStore.tabs.length > 0">
    <div class="tab-list" ref="scrollRef">
      <div
        v-for="(tab, index) in tabsStore.tabs"
        :key="tab.path"
        class="tab-item"
        :class="{ active: tabsStore.activePath === tab.path, affix: tab.affix }"
        :data-idx="index"
        @click="switchTab(tab.path)"
        @contextmenu.prevent="openCtxMenu($event, tab)"
        @mouseenter="hoverTab = tab.path"
        @mouseleave="hoverTab = ''"
      >
        <span v-if="tab.affix" class="tab-affix-dot"></span>
        <span class="tab-title">{{ tab.title }}</span>
        <span
          v-if="!tab.affix && (hoverTab === tab.path || tabsStore.activePath === tab.path)"
          class="tab-close"
          @click.stop="closeTab(tab.path)"
        >
          <el-icon :size="10"><Close /></el-icon>
        </span>
      </div>
    </div>
    <!-- 右侧单个下拉按钮（ADP 同款）：点开当前标签的操作菜单 -->
    <div class="tab-actions">
      <el-tooltip content="标签操作" placement="top" :show-after="300">
        <span class="tab-action-btn" @click.stop="openCtxMenuAtActive($event)">
          <el-icon :size="16"><ArrowDown /></el-icon>
        </span>
      </el-tooltip>
    </div>

    <!-- 右键/下拉菜单（ADP 六项同款） -->
    <teleport to="body">
      <div
        v-if="ctxVisible"
        ref="menuRef"
        class="tab-ctx-menu"
        :style="{ left: ctxX + 'px', top: ctxY + 'px' }"
      >
        <div class="ctx-item" :class="{ disabled: !ctxIsCurrent }" @click="handleRefresh">
          <el-icon><RefreshRight /></el-icon>
          <span>刷新页面</span>
        </div>
        <div
          class="ctx-item"
          :class="{ disabled: ctxTab?.affix }"
          @click="handleToggleAffix"
        >
          <el-icon><Star /></el-icon>
          <span>{{ ctxTab?.affix ? '取消固定' : '固定标签' }}</span>
        </div>
        <div class="ctx-divider"></div>
        <div
          class="ctx-item"
          :class="{ disabled: !canCloseLeft }"
          @click="handleCtxCloseLeft"
        >
          <el-icon><ArrowLeft /></el-icon>
          <span>关闭左侧</span>
        </div>
        <div
          class="ctx-item"
          :class="{ disabled: !canCloseRight }"
          @click="handleCtxCloseRight"
        >
          <el-icon><ArrowRight /></el-icon>
          <span>关闭右侧</span>
        </div>
        <div
          class="ctx-item"
          :class="{ disabled: tabsStore.tabs.length <= 1 }"
          @click="handleCtxCloseOthers"
        >
          <el-icon><CircleClose /></el-icon>
          <span>关闭其他</span>
        </div>
        <div
          class="ctx-item"
          :class="{ disabled: tabsStore.tabs.length <= 1 }"
          @click="handleCtxCloseAll"
        >
          <el-icon><Delete /></el-icon>
          <span>关闭全部</span>
        </div>
      </div>
    </teleport>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, nextTick, watch, onMounted, onUnmounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import {
  Close,
  CircleClose,
  Delete,
  RefreshRight,
  Star,
  ArrowDown,
  ArrowLeft,
  ArrowRight
} from '@element-plus/icons-vue'
import { useTabsStore, type TabItem } from '@/stores/tabs'

const router = useRouter()
const route = useRoute()
const tabsStore = useTabsStore()

const hoverTab = ref('')

const switchTab = (path: string) => {
  if (path !== tabsStore.activePath) router.push(path)
}

const closeTab = (path: string) => {
  const next = tabsStore.closeTab(path)
  if (next) router.push(next)
}

/** 刷新当前页（通过重定向中转实现组件重建） */
const refreshing = ref(false)
const handleRefresh = () => {
  if (!ctxIsCurrent.value) return
  refreshing.value = true
  closeCtxMenu()
  router.replace({ path: '/redirect' + tabsStore.activePath })
}

// ===== 固定/取消固定 =====
const handleToggleAffix = () => {
  if (!ctxTab.value || ctxTab.value.affix) return
  const t = tabsStore.tabs.find(x => x.path === ctxTab.value!.path)
  if (t) t.affix = !t.affix
  closeCtxMenu()
}

// ===== 右键/下拉菜单 =====
const ctxVisible = ref(false)
const ctxX = ref(0)
const ctxY = ref(0)
const ctxTab = ref<TabItem | null>(null)

const ctxIsCurrent = computed(() => ctxTab.value?.path === tabsStore.activePath)

/** 激活标签左侧是否还有可关闭标签 */
const canCloseLeft = computed(() => {
  if (!ctxTab.value) return false
  const idx = tabsStore.tabs.findIndex(t => t.path === ctxTab.value!.path)
  return idx > 0 && tabsStore.tabs.some((t, i) => i < idx && !t.affix)
})

/** 激活标签右侧是否还有可关闭标签 */
const canCloseRight = computed(() => {
  if (!ctxTab.value) return false
  const idx = tabsStore.tabs.findIndex(t => t.path === ctxTab.value!.path)
  return idx >= 0 && idx < tabsStore.tabs.length - 1
})

const openCtxMenu = (e: MouseEvent, tab: TabItem) => {
  ctxX.value = e.clientX
  ctxY.value = e.clientY
  ctxTab.value = tab
  ctxVisible.value = true
  clampMenuToViewport()
}

const openCtxMenuAtActive = (e: MouseEvent) => {
  const active = tabsStore.tabs.find(t => t.path === tabsStore.activePath)
  if (!active) return
  const rect = (e.currentTarget as HTMLElement).getBoundingClientRect()
  ctxX.value = rect.left
  ctxY.value = rect.bottom + 4
  ctxTab.value = active
  ctxVisible.value = true
  clampMenuToViewport()
}

// 菜单贴屏幕右/下边缘时收回可视区（防被截断）
const menuRef = ref<HTMLElement | null>(null)
const clampMenuToViewport = () => {
  nextTick(() => {
    const el = menuRef.value
    if (!el) return
    const r = el.getBoundingClientRect()
    let x = ctxX.value
    let y = ctxY.value
    if (x + r.width > window.innerWidth - 8) x = window.innerWidth - r.width - 8
    if (y + r.height > window.innerHeight - 8) y = window.innerHeight - r.height - 8
    if (x < 8) x = 8
    if (y < 8) y = 8
    ctxX.value = x
    ctxY.value = y
  })
}

const closeCtxMenu = () => {
  ctxVisible.value = false
}

// ===== 关闭操作（关的是点击的那个标签；若当前激活页被关，跳到点击页） =====
const handleCtxCloseLeft = () => {
  if (!ctxTab.value || !canCloseLeft.value) return
  const jumped = tabsStore.closeLeft(ctxTab.value.path)
  if (jumped) router.push(jumped)
  closeCtxMenu()
}

const handleCtxCloseRight = () => {
  if (!ctxTab.value || !canCloseRight.value) return
  const jumped = tabsStore.closeRight(ctxTab.value.path)
  if (jumped) router.push(jumped)
  closeCtxMenu()
}

const handleCtxCloseOthers = () => {
  if (!ctxTab.value || tabsStore.tabs.length <= 1) return
  const keep = ctxTab.value.path
  tabsStore.closeOthers(keep)
  if (keep !== tabsStore.activePath) router.push(keep)
  closeCtxMenu()
}

const handleCtxCloseAll = () => {
  if (tabsStore.tabs.length <= 1) return
  const next = tabsStore.closeAll()
  if (next !== tabsStore.activePath) router.push(next)
  closeCtxMenu()
}

const handleDocClick = () => closeCtxMenu()

// ===== 滚轮横向滚动 + 激活标签自动滚动到可视区（ADP 同款） =====
const scrollRef = ref<HTMLElement | null>(null)

const handleWheel = (e: WheelEvent) => {
  const el = scrollRef.value
  if (!el) return
  if (el.scrollWidth <= el.clientWidth) return
  e.preventDefault()
  const delta = Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : e.deltaY
  el.scrollLeft += delta
}

const scrollToActive = () => {
  nextTick(() => {
    const el = scrollRef.value
    if (!el) return
    const activeEl = el.querySelector('.tab-item.active') as HTMLElement | null
    if (!activeEl) return
    // 激活标签超出可视范围时，滚到它刚好可见
    if (activeEl.offsetLeft < el.scrollLeft) {
      el.scrollTo({ left: activeEl.offsetLeft - 8, behavior: 'smooth' })
    } else if (activeEl.offsetLeft + activeEl.offsetWidth > el.scrollLeft + el.clientWidth) {
      el.scrollTo({
        left: activeEl.offsetLeft + activeEl.offsetWidth - el.clientWidth + 8,
        behavior: 'smooth'
      })
    }
  })
}

watch(() => route.path, scrollToActive)
onMounted(() => {
  document.addEventListener('click', handleDocClick)
  scrollRef.value?.addEventListener('wheel', handleWheel, { passive: false })
  scrollToActive()
})
onUnmounted(() => {
  document.removeEventListener('click', handleDocClick)
  scrollRef.value?.removeEventListener('wheel', handleWheel)
})

defineExpose({ refreshing })
</script>

<style scoped>
/* 顶栏内的第二行：随顶栏整块固定，自身不再定位 */
.tab-bar {
  display: flex;
  align-items: center;
  background: var(--color-bg-primary, #ffffff);
  padding: 5px 16px;
  gap: 8px;
}

.tab-list {
  display: flex;
  align-items: center;
  gap: 6px;
  overflow-x: auto;
  flex: 1;
  scrollbar-width: none;
}

.tab-list::-webkit-scrollbar {
  height: 0;
}

/* ADP art-card-xs 同款：白底 + 1px 淡描边 + 圆角小卡片，无加粗 */
.tab-item {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  height: 30px;
  padding: 0 9px 0 11px;
  border: 1px solid var(--art-card-border, rgba(0, 0, 0, 0.08));
  border-radius: 8px;
  background: var(--color-bg-primary, #ffffff);
  color: var(--color-text-regular, #606266);
  font-size: 12.5px;
  cursor: pointer;
  white-space: nowrap;
  transition: background-color 0.2s ease, color 0.2s ease, border-color 0.2s ease;
  user-select: none;
}

.tab-item:hover {
  background: #f5f6f7;
  color: var(--color-primary, #2383e2);
  border-color: var(--art-card-border, rgba(0, 0, 0, 0.08));
}

/* 激活态：ADP 同款 = 主题色文字 + 淡主题色背景，不加粗 */
.tab-item.active {
  background: var(--color-primary-lighter, #eaf3fc);
  border-color: var(--color-primary-lighter, #eaf3fc);
  color: var(--color-primary, #2383e2);
  font-weight: 400;
}

.tab-affix-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--color-primary, #2383e2);
  flex-shrink: 0;
}

/* 关闭按钮：激活/hover 时才显示，圆形 hover 底（ADP 同款） */
.tab-close {
  width: 15px;
  height: 15px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: inherit;
  opacity: 0.65;
  transition: background-color 0.15s ease, opacity 0.15s ease;
}

.tab-close:hover {
  background: var(--color-border-light, #e4e7ed);
  opacity: 1;
}

.tab-item.active .tab-close:hover {
  background: rgba(35, 131, 226, 0.18);
}

.tab-actions {
  display: flex;
  gap: 4px;
  flex-shrink: 0;
}

.tab-action-btn {
  width: 30px;
  height: 30px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--art-card-border, rgba(0, 0, 0, 0.08));
  border-radius: 8px;
  color: var(--color-text-secondary, #909399);
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease;
}

.tab-action-btn:hover {
  background: var(--color-primary-lighter, #eaf3fc);
  color: var(--color-primary-dark, #165fae);
}

/* 右键菜单（ADP 同款圆角+分组线） */
.tab-ctx-menu {
  position: fixed;
  z-index: 3000;
  background: var(--color-bg-primary, #ffffff);
  border: 1px solid var(--color-border-light, #e4e7ed);
  border-radius: 10px;
  box-shadow: var(--shadow-md, 0 4px 16px rgba(0, 0, 0, 0.12));
  padding: 5px;
  min-width: 132px;
}

.ctx-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 7px 12px;
  font-size: 13px;
  color: var(--color-text-regular, #606266);
  border-radius: 6px;
  cursor: pointer;
}

.ctx-item:hover {
  background: var(--color-primary-lighter, #eaf3fc);
  color: var(--color-primary-dark, #165fae);
}

.ctx-item.disabled {
  color: var(--color-text-placeholder, #c0c4cc);
  cursor: not-allowed;
}

.ctx-item.disabled:hover {
  background: transparent;
  color: var(--color-text-placeholder, #c0c4cc);
}

.ctx-divider {
  height: 1px;
  background: var(--color-border-lighter, #ebeef5);
  margin: 4px 8px;
}
</style>
